import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function RetailEcommerceExample() {
  useSEO({
    title: 'Retail & E-Commerce Simulation',
    description: 'Simulate seasonal trends, promotions, and customer behavior. Forecast demand and price with confidence.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Demand Forecasting + Pricing</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">Forecast demand and price with confidence</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">Simulate seasonal trends, promotions, and customer behavior to stock the right products at the right price.</p>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Merchandising Leaders</p><p className="text-sm text-gray-600">Optimizing assortment and allocation</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Pricing Managers</p><p className="text-sm text-gray-600">Setting prices that maximize margin</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Demand Planners</p><p className="text-sm text-gray-600">Forecasting across channels and seasons</p></div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How much inventory to buy for each season</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">When to trigger markdowns vs. hold prices</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How promotions affect demand cannibalization</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Which products to feature in each channel</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How competitor pricing affects your sales</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Trade-offs between volume and margin</p></div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: E-commerce Retailer</p>
            <p className="text-lg leading-relaxed mb-6">An e-commerce retailer simulated Black Friday demand patterns, markdown timing strategies, and warehouse allocation decisions. The model identified that starting markdowns three days earlier on slow-moving categories—while holding prices on trending items—would boost revenue by 12% while reducing end-of-season clearance inventory by 35%.</p>
            <p className="text-gray-400 text-sm">The impact: $3.2M additional margin with improved inventory turns.</p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Model your demand patterns, test pricing scenarios, and share insights with merchandising teams. Ideal for retailers who want ongoing forecasting capability."
          consultingDescription="Partner with our retail consultants to build a custom demand model, run pricing workshops, and deliver margin-optimizing recommendations."
        />

        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/supply-chain" className="text-gray-700 hover:text-gray-900 text-sm">Inventory + Production →</Link>
            <Link to="/solutions/logistics-transportation" className="text-gray-700 hover:text-gray-900 text-sm">Route + Fleet Optimization →</Link>
            <Link to="/solutions/revenue-pipeline" className="text-gray-700 hover:text-gray-900 text-sm">Pipeline + Revenue Forecasting →</Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default RetailEcommerceExample;
